import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import Link from "next/link";
import { useState } from "react";

import PosLayout from "../../components/layouts/posLayout.comp";
import { listCompletedPurchaseOrders, purchaseOrderIdString } from "../../core/purchaseOrder.core";
import { useTranslation } from "../../core/translation.core";
import useGetRetailShopId from "../../hooks/useGetRetailShopId.hook";

export default function PurchaseOrderListPage() {
  const { t, msg } = useTranslation();
  return (
    <PosLayout title={t(msg.purchaseOrder.title)} showback>
      <Content />
    </PosLayout>
  );
}

function Content() {
  const { t, msg } = useTranslation();
  const retailId = useGetRetailShopId();
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const { data, isPending, isError, error } = useQuery({
    enabled: !!retailId,
    queryKey: ["purchaseOrderList", retailId, searchTerm],
    queryFn: () =>
      listCompletedPurchaseOrders({
        paging: { page: 1, size: 100 },
        ...(searchTerm ? { nameNumberProductSupplier: searchTerm } : {}),
      }),
  });

  function runSearch() {
    setSearchTerm(searchInput.trim());
  }

  return (
    <main className="dark:text-white space-y-6 p-2 lg:p-4 max-w-4xl" data-role="purchase-order-list-page">
      <section data-type="section" className="space-y-4">
        <h1 className="text-2xl font-bold tracking-tight">{t(msg.purchaseOrder.title)}</h1>

        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            placeholder={t(msg.purchaseOrder.searchPoNumber)}
            className="flex-1 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-her-darkblue2 px-3 py-2 text-sm"
            data-role="purchase-order-search-input"
          />
          <button
            type="button"
            onClick={runSearch}
            className="rounded-lg bg-her-blue text-white px-4 py-2 font-medium shrink-0"
            data-role="purchase-order-search-btn"
          >
            {t(msg.country.search)}
          </button>
        </div>
      </section>

      <section data-type="section" className="space-y-4">
        {isPending && <p className="text-sm opacity-70">{t(msg.form.loading)}</p>}

        {isError && (
          <p className="text-sm text-red-500" data-role="purchase-order-list-error">
            {error instanceof Error ? error.message : String(error)}
          </p>
        )}

        {!isPending && !isError && data && data.list.length === 0 && (
          <p className="text-sm opacity-80" data-role="purchase-order-empty">
            {t(msg.purchaseOrder.noOrders)}
          </p>
        )}

        {!isPending && data && data.list.length > 0 && (
          <div className="space-y-2" data-role="purchase-order-list">
            {data.list.map((row) => (
              <div key={purchaseOrderIdString(row.id)}>
                <Link
                  href={`/purchaseOrder/${purchaseOrderIdString(row.id)}`}
                  className="block rounded-lg bg-white dark:bg-her-darkblue2 p-2"
                  data-role={`purchase-order-row-${purchaseOrderIdString(row.id)}`}
                  data-order-number={row.orderNumber}
                >
                  <div className="flex flex-wrap justify-between gap-2">
                    <span className="font-semibold">{row.orderNumber}</span>
                    <span className="text-sm rounded-full bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200 px-3 py-0.5">
                      {t(msg.order.completed)}
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs opacity-80">
                    <span>
                      {t(msg.purchaseOrder.deliverTo)}: {row.warehouse?.name ?? "—"}
                    </span>
                    <span>
                      {t(msg.purchaseOrder.arrivalDate)}:{" "}
                      {row.arrivalDate ? format(new Date(row.arrivalDate), "yyyy-MM-dd") : "—"}
                    </span>
                    <span>
                      {row.itemCount} {t(msg.pos.items)}
                    </span>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
